/** Button component: the four variants used across the app. */
import type { ButtonHTMLAttributes, ReactNode } from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'ghost';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Visual style. Defaults to `"primary"` (dominant violet). */
  variant?: ButtonVariant;
  /** Stretches the button to the full width of its container. */
  fullWidth?: boolean;
  /** Button label and/or icon. */
  children: ReactNode;
}

/**
 * Render a charter-styled button: uppercase display type, hairline border,
 * stamp shadow — the one surface that keeps it, so that what is pressable
 * reads as pressable — and a ≥44px touch target for mobile field use.
 *
 * @param props - Button properties; unknown props are spread on the
 *   underlying `<button>` element.
 * @returns The button element.
 */
export function Button({
  variant = 'primary',
  fullWidth = false,
  className,
  children,
  ...rest
}: ButtonProps) {
  const classes = ['ui-btn', `ui-btn--${variant}`, fullWidth ? 'ui-btn--full' : '', className]
    .filter(Boolean)
    .join(' ');

  return (
    <button className={classes} {...rest}>
      {children}
    </button>
  );
}
